import React, { useEffect, useRef, useState } from "react";

interface LocationPoint {
  id: string;
  name: string;
  type: "loading" | "unloading" | "clearance" | "pickup";
  lat: number;
  lng: number;
  address: string;
}

interface RouteMapProps {
  pickupPoint?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
  loadingPoint?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
  unloadingPoint?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
  clearancePoint?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
}

const RouteMap: React.FC<RouteMapProps> = ({
  pickupPoint,
  loadingPoint,
  unloadingPoint,
  clearancePoint,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [activePointId, setActivePointId] = useState<string | null>(null);

  // Build route points array from props
  const getRoutePoints = (): LocationPoint[] => {
    const points: LocationPoint[] = [];

    if (pickupPoint && pickupPoint.lat != null && pickupPoint.lng != null) {
      points.push({
        id: "pickup",
        name: "Pickup Point",
        type: "pickup",
        lat: pickupPoint.lat,
        lng: pickupPoint.lng,
        address: pickupPoint.address || "Pickup Point"
      });
    }

    if (loadingPoint && loadingPoint.lat != null && loadingPoint.lng != null) {
      points.push({
        id: "loading",
        name: "Loading Point",
        type: "loading",
        lat: loadingPoint.lat,
        lng: loadingPoint.lng,
        address: loadingPoint.address || "Loading Point"
      });
    }

    if (clearancePoint && clearancePoint.lat != null && clearancePoint.lng != null) {
      points.push({
        id: "clearance",
        name: "Clearance Point",
        type: "clearance",
        lat: clearancePoint.lat,
        lng: clearancePoint.lng,
        address: clearancePoint.address || "Clearance Point"
      });
    }

    if (unloadingPoint && unloadingPoint.lat != null && unloadingPoint.lng != null) {
      points.push({
        id: "unloading",
        name: "Unloading Point",
        type: "unloading",
        lat: unloadingPoint.lat,
        lng: unloadingPoint.lng,
        address: unloadingPoint.address || "Unloading Point"
      });
    }

    return points;
  };

  // Get current route points
  const routePoints = getRoutePoints();

  // Reset and initialize the map with new points
  const initializeMap = async () => {
    if (!window.google?.maps) {
      console.error("Google Maps API not loaded");
      return;
    }

    try {
      const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

      // Clear existing markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Clear existing polyline
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }

      const points = getRoutePoints();

      // If no points yet, return
      if (points.length === 0) {
        setIsLoading(false);
        return;
      }

      // Create bounds for the map
      const bounds = new google.maps.LatLngBounds();
      points.forEach(point => {
        bounds.extend({ lat: point.lat, lng: point.lng });
      });

      // Init map if not already initialized
      if (!mapRef.current) {
        mapRef.current = new Map(document.getElementById("map")!, {
          center: bounds.getCenter(),
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });
      }

      // Fit bounds to show all points
      if (points.length > 1) {
        mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      } else if (points.length === 1) {
        mapRef.current.setCenter({ lat: points[0].lat, lng: points[0].lng });
        mapRef.current.setZoom(15);
      }

      // Define icon colors
      const iconColors = {
        loading: "#10b981", // Green
        unloading: "#ef4444", // Red
        clearance: "#3b82f6", // Blue
        pickup: "#f59e0b", // Amber
      };

      // Add markers for each point
      points.forEach(point => {
        const marker = new google.maps.Marker({
          position: { lat: point.lat, lng: point.lng },
          map: mapRef.current,
          title: point.name,
          animation: google.maps.Animation.DROP,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: iconColors[point.type],
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: "#ffffff",
            scale: 10,
          },
        });

        // Add click listener to marker
        marker.addListener("click", () => {
          setActivePointId(point.id);
        });

        markersRef.current.push(marker);
      });

      // Draw route line connecting the points if more than one point
      if (points.length > 1) {
        const path = points.map(point => ({ lat: point.lat, lng: point.lng }));
        polylineRef.current = new google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: "#3b82f6",
          strokeOpacity: 0.8,
          strokeWeight: 3,
          map: mapRef.current,
        });
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error initializing map:", error);
      setIsLoading(false);
    }
  };

  // Initialize map on mount
  useEffect(() => {
    const loadMap = () => {
      if (window.google && window.google.maps) {
        initializeMap();
      } else {
        // Set a timeout to check again
        setTimeout(loadMap, 500);
      }
    };

    loadMap();

    return () => {
      // Clean up
      markersRef.current.forEach(marker => marker.setMap(null));
      if (polylineRef.current) polylineRef.current.setMap(null);
    };
  }, []);

  // Update map when route points change
  useEffect(() => {
    if (window.google && window.google.maps) {
      initializeMap();
    }
  }, [pickupPoint, loadingPoint, unloadingPoint, clearancePoint]);

  // Get the active point details
  const activePoint = activePointId ? routePoints.find(p => p.id === activePointId) : null;

  // Color mapping for the legend and InfoWindow
  const typeColors = {
    loading: "bg-green-500",
    unloading: "bg-red-500",
    clearance: "bg-blue-500",
    pickup: "bg-amber-500",
  };

  const typeLabels = {
    loading: "Loading",
    unloading: "Unloading",
    clearance: "Clearance",
    pickup: "Pickup",
  };

  return (
    <div className="w-full h-full relative rounded-lg overflow-hidden border border-gray-200">
      {/* Map Container */}
      <div
        id="map"
        className="w-full h-full rounded-lg"
        style={{
          filter: isLoading ? "blur(2px)" : undefined,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center rounded-lg">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* No Points Message */}
      {!isLoading && routePoints.length === 0 && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg">
          <div className="text-gray-500 text-center p-4">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
            </svg>
            <p className="font-medium">No route points selected</p>
            <p className="text-sm mt-1">Please select pickup, loading, clearance, and unloading points to view the route.</p>
          </div>
        </div>
      )}

      {/* Legend */}
      {routePoints.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 p-3 rounded-lg shadow-md text-sm">
          <div className="font-medium mb-2">Route Points</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {routePoints.map(point => (
              <div key={point.id} className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${typeColors[point.type]} mr-2`}></div>
                <span>{typeLabels[point.type]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Point Info */}
      {activePoint && (
        <div className="absolute top-4 right-4 bg-white bg-opacity-90 p-3 rounded-lg shadow-md text-sm max-w-xs">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full ${typeColors[activePoint.type]} mr-2`}></div>
            <div className="font-medium">{activePoint.name}</div>
          </div>
          <div className="mt-1 text-xs">{activePoint.address}</div>
          <div className="text-gray-500 text-xs mt-1">
            Lat: {activePoint.lat.toFixed(4)}, Lng: {activePoint.lng.toFixed(4)}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteMap;